import React, { useState, useEffect } from 'react';
import { Row, Col } from '@zendeskgarden/react-grid';
import { BtnGroup } from 'components//Forms/BtnGroup/BtnGroup.styled';
import { Button } from '@zendeskgarden/react-buttons';
import { Form, Subheading } from 'components/Forms/Forms.styled';
import {
  DropdownField,
  InputField,
  DateRangeDropdownField,
  AutoCompleteDropdownField,
} from 'components/Forms/Fields';
import {
  getHealthProblems,
  getMaterialTypes,
  getLaboratory,
} from 'containers/AgroBRF/FlockList/service';
import { Separator } from 'containers/AgroBRF/FlockList/Flock/Flock.styled';
import MedicationConfirmation from './MedicationConfirmation';
import t from 'components/i18n';

export default function MedicationForm({
  houses,
  goTo,
  isEditing = false,
  isValid,
  medication,
  selectedMedicationId,
  setSelectedMedicationId,
  sendToConfirmation,
  setSendToConfirmation,
  isSubmitting,
  setFieldValue,
  handleSubmit,
  handleChange,
  handleBlur,
  resetForm,
  values,
  errors,
  touched,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [materialList, setMaterialList] = useState([]);
  const [laboratoryList, setLaboratoryList] = useState([]);
  const [healthProblems, setHealthProblems] = useState([]);

  async function getMaterialTypesList() {
    setIsLoading(true);
    try {
      const response = await getMaterialTypes();
      setMaterialList(response);
    } catch (e) {
      console.error('Error on getSummary', e);
    }
    setIsLoading(false);
  }

  async function getLaboratoryList() {
    setIsLoading(true);
    try {
      const response = await getLaboratory();
      setLaboratoryList(response);
    } catch (e) {
      console.error('Error on getSummary', e);
    }
    setIsLoading(false);
  }

  async function getHealthProblemsList() {
    setIsLoading(true);
    try {
      const response = await getHealthProblems();
      setHealthProblems(response);
    } catch (e) {
      console.error('Error on getSummary', e);
    }
    setIsLoading(false);
  }

  // set houses
  const housesArray = houses;
  housesArray.forEach(
    house => (house.label = t('form.agro.swine-medication.house', { value: house.value })),
  );

  useEffect(() => {
    if (!sendToConfirmation && houses.length > 0) {
      const house = houses.filter(
        item => values.houseId === item.id,
      )[0];

      setFieldValue('house', house);
    }

    if (sendToConfirmation) {
      if (values.house) {
        setFieldValue('houseId', values.house.id);
      }
    }
  }, [
    houses,
    sendToConfirmation,
    setFieldValue,
    values.house,
    values.houseId,
  ]);

  useEffect(() => {
    if (values.id) {
      setFieldValue('id', values.id);
    } else {
      setFieldValue('enableEdit', true);
    }
  }, [setFieldValue, values.id]);

  useEffect(() => {
    if (materialList) {
      const favoriteMaterial = materialList.filter(e => e.isFavorite);

      if (
        favoriteMaterial.length > 0 &&
        !values.material &&
        !values.id
      ) {
        setFieldValue('material', favoriteMaterial[0]);
      }
    }
  }, [materialList, setFieldValue, values]);

  useEffect(() => {
    if (!sendToConfirmation) {
      getMaterialTypesList();
      getHealthProblemsList();
      getLaboratoryList();
    }
  }, [sendToConfirmation]);

  return (
    values && (
      <Form autoComplete="off">
        {!sendToConfirmation ? (
          <>
            <Row>
              <Col md={6}>
                {/* Instalação */}
                <DropdownField
                  field="house"
                  label={t('form.agro.swine-medication.field.house.label')}
                  placeholder={t('form.agro.swine-medication.field.house.placeholder')}
                  onSelect={item => {
                    setFieldValue('house', item);
                  }}
                  disabledItems="show"
                  options={housesArray}
                  optionLabel="label"
                  values={values}
                  errors={errors}
                  touched={touched}
                />
                {/* \ Instalação */}
              </Col>

              <Col md={6}>
                {/* Data */}
                <DateRangeDropdownField
                  field="treatmentPeriod"
                  label={t('form.agro.swine-medication.field.treatmentPeriod.label')}
                  placeholder={t('form.agro.swine-medication.field.treatmentPeriod.placeholder')}
                  buttonLabel={t('global.button.filter.date')}
                  keysToCompare={['startDate', 'endDate']}
                  onChange={item => {
                    setFieldValue('treatmentPeriod', item);
                  }}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
                {/* \ Data */}
              </Col>
            </Row>

            <Separator gap={8} />
            <br />

            <Subheading>{t('form.agro.swine-medication.field.medicine.section')}</Subheading>

            <Row>
              <Col>
                <AutoCompleteDropdownField
                  field="material"
                  label={t('form.agro.swine-medication.field.material.label')}
                  placeholder={t('form.agro.swine-medication.field.material.placeholder')}
                  onSelect={e => {
                    setFieldValue('material', e);
                  }}
                  loadingLabel={t('global.loading')}
                  optionLabel="description"
                  caption="code"
                  noMatchLabel={t('global.no-content')}
                  selectObject
                  options={materialList}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <AutoCompleteDropdownField
                  field="laboratory"
                  label={t('form.agro.swine-medication.field.laboratory.label')}
                  placeholder={t('form.agro.swine-medication.field.laboratory.placeholder')}
                  onSelect={e => {
                    setFieldValue('laboratory', e);
                  }}
                  loadingLabel={t('global.loading')}
                  optionLabel="description"
                  noMatchLabel={t('global.no-content')}
                  selectObject
                  options={laboratoryList}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <InputField
                  type="text"
                  field="vaccineBatch"
                  label={t('form.agro.swine-medication.field.vaccineBatch.label')}
                  placeholder={t('form.agro.swine-medication.field.vaccineBatch.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <InputField
                  type="number"
                  field="quantity"
                  label={t('form.agro.swine-medication.field.quantity.label')}
                  placeholder={t('form.agro.swine-medication.field.quantity.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Separator gap={8} />
            <br />

            <Subheading>{t('form.agro.swine-medication.field.treatment.section')}</Subheading>

            <Row>
              <Col>
                <AutoCompleteDropdownField
                  field="healthProblem"
                  label={t("form.agro.swine-medication.field.healthProblem.label")}
                  placeholder={t("form.agro.swine-medication.field.healthProblem.placeholder")}
                  onSelect={e => {
                    setFieldValue('healthProblem', e);
                  }}
                  loadingLabel={t('global.loading')}
                  optionLabel="name"
                  noMatchLabel={t('global.no-content')}
                  selectObject
                  options={healthProblems}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Row>
              <Col>
                <InputField
                  type="number"
                  field="quantityTreated"
                  label={t('form.agro.swine-medication.field.quantityTreated.label')}
                  placeholder={t('form.agro.swine-medication.field.quantityTreated.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  values={values}
                  errors={errors}
                  touched={touched}
                />
              </Col>
            </Row>

            <Separator />

            {/* \ Botões */}
            <BtnGroup>
              <Button
                key="sendtoConfirm"
                onClick={() => {
                  setSendToConfirmation(true);
                }}
                disabled={!isValid}
              >
                {t("global.button.send")}
              </Button>
              <Button
                key="clean"
                onClick={() => {
                  resetForm();
                }}
                className="white"
              >
                {t("global.button.clear")}
              </Button>
            </BtnGroup>
            {/* \ Botões */}
          </>
        ) : (
          <MedicationConfirmation
            values={{ ...values }}
            isSubmitting={isSubmitting}
            backToForm={() => {
              setSendToConfirmation(false);
            }}
            onSubmit={handleSubmit}
            isLoading={isLoading}
            isEditing={isEditing}
            isValid={isValid}
            goTo={goTo}
            medication={medication}
            setSelectedMedicationId={setSelectedMedicationId}
            selectedMedicationId={selectedMedicationId}
            isEditable={values.isEditable}
            setSendToConfirmation={setSendToConfirmation}
          />
        )}
      </Form>
    )
  );
}
